/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This file corresponds to the Home Page content (without any folder selected)
 * The URI keyword would have to match something like 'accueil'
 * It will show the new published documents by default and can be switch
 * to document deposit form or to sent documents list
 */

// Imports
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faDownload, faPrint } from '@fortawesome/free-solid-svg-icons';

// Sides
import { Header } from '../sides/Header';
import { SideBar } from '../sides/SideBar';

// Sub components
import { DocumentDeposit } from '../subpages/DocumentDeposit';
import { SentDocuments } from '../subpages/SentDocuments';

// Interfaces
import { PublicCabinet } from '../../interfaces/ICabinets';
import { Dossier } from '../../interfaces/IDossiers';
import { Document } from '../../interfaces/IDocuments';
import { AlertType, MySpaceRenderableComponent, SortOrder, SortType, TableRow } from '../../interfaces/IUtils';


// Utils
import { HTTPResponse, downloadDocument, fetchDossiers, fetchNewDocs, fetchUpcomingActions } from '../utils/ApiCallConstructor';
import { dateToDDMMYYYY, getExtFromFilename, getTypeImg } from '../utils/Format';
import { ErrorForUsr, displayAlert, signOut } from '../utils/Errors';

// Buroclic adaptive components
import { BCTable } from '../buroclic/BCTable';
import { Action } from '../../interfaces/IActions';
import { printElement } from '../utils/Miscellaneous';
import { BCTutorials } from '../subpages/BCTutorials';

export const HomePage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  if (state?.error) displayAlert({ message: state?.error, type: AlertType.error });

  const cabinetAlias = localStorage.getItem('cabinetAlias');
  const cabinet = JSON.parse(localStorage.getItem('cabinet') || "{}") as PublicCabinet;
  document.title = cabinetAlias ? cabinetAlias + ' | Espace client' : 'Votre espace client';

  const [isDownloading, setIsDownloading] = useState(false);
  const dossier: Dossier = state?.dossier || {} as Dossier;
  const [dossiers, setDossiers] = useState<Dossier[]>([]);
  const [sidebarExpanded, setSidebarExpanded] = useState<boolean>(state?.expanded || false);

  // New published documents relative states/functions
  const [newDocuments, setNewDocuments] = useState<Document[]>([]);
  const [tableRows, setTableRows] = useState<TableRow[]>([]);

  // Format new Documents as wanted by the BCTable
  useEffect(() => {
    const tmpRows: TableRow[] = [];
    newDocuments.forEach(newDoc => {
      tmpRows.push({
        'id': newDoc.DOC_REF,
        'Date': newDoc.DOC_DATE,
        'DOS_REF': newDoc.DOS_REF || 0,
        'Dossier': newDoc.DOS_CODE + ' - ' + newDoc.DOS_TITRE,
        'Type': getExtFromFilename(newDoc.DOC_FILE),
        'Nom': newDoc.DOC_NOM || ''
      });
    });
    console.log('Docs to display : ', tmpRows);
    setTableRows(tmpRows);
  }, [newDocuments]);


  // New published actions relative states/functions
  const [upcomingActions, setUpcomingActions] = useState<Action[]>([]);
  const [actionsTableRows, setActionsTableRows] = useState<TableRow[]>([]);

  // Format new Documents as wanted by the BCTable
  useEffect(() => {
    const tmpRows: TableRow[] = [];
    upcomingActions.forEach(upcomingAction => {
      tmpRows.push({
        'id': upcomingAction.ACT_ID,
        'Date': upcomingAction.ACT_DATE,
        'Lieu': upcomingAction.ACT_LIEU ?? '',
        'DOS_REF': upcomingAction.DOS_REF ?? 0,
        'Dossier': upcomingAction.DOS_CODE + ' - ' + upcomingAction.DOS_TITRE,
        'Descriptif': upcomingAction.ACT_LIBELLE,
        'Intervenant': upcomingAction.ACT_ACTEUR,
        'Commentaire': upcomingAction.ACT_DESCRIPTION ?? ''
      });
    });
    setActionsTableRows(tmpRows);
  }, [upcomingActions]);


  // Get initial data
  useEffect(() => {

    // Get folders/dossiers from BACK or localstorage, otherwise, display error and handle session expiration
    fetchDossiers().then((fetchedDossiers) => setDossiers(fetchedDossiers))
      .catch((err: any) => {
        displayAlert({ message: err.error, type: AlertType.error });
        if (err.error === ErrorForUsr.NotSignedIn)
          signOut((cabAlias) => navigate(`/${cabAlias}/login`));
      });


    // Get new documents from BACK or localstorage, otherwise, display error and handle session expiration
    fetchNewDocs().then((documents: Document[]) => { console.log('Step E - ', documents); setNewDocuments(documents); })
      .catch((err: any) => {
        displayAlert({ message: err.error, type: AlertType.error })
        if (err.error === ErrorForUsr.NotSignedIn)
          signOut((cabAlias) => navigate(`/${cabAlias}/login`));
      });

    // If feature is ON, get new actions from BACK or localstorage (or display errr and handle session expiration)
    if (cabinet.permissions?.isPlanningEnabled) {
      fetchUpcomingActions().then((actions) => setUpcomingActions(actions))
        .catch((err: any) => {
          displayAlert({ message: err.error, type: AlertType.error })
          if (err.error === ErrorForUsr.NotSignedIn)
            signOut((cabAlias) => navigate(`/${cabAlias}/login`));
        });
    }
  }, [navigate, cabinet.permissions?.isPlanningEnabled]);



  // Download documents the user has selected
  // BE AWARE : Downloading more than 10 documents at the same time seems to
  // skip the download of some of the wanted documents. In order to prevent that
  // You will have to uncomment the timeout of 1s every 10 downloads
  // But this is useful only if the pagination is showing more than 10 documents at a time
  const DownloadSelectedDocs = async (selectedRow: TableRow[]) => {
    if (isDownloading) return; // Prevent multiple download

    try {
      // Must download a SELECTED document
      if (selectedRow.length <= 0)
        return;

      setIsDownloading(true);

      // Download each document one by one
      await toast.promise(
        Promise.all(
          selectedRow.map(async row => {
            try { await downloadDocument(newDocuments.filter(doc => doc.DOC_REF === row.id)[0], navigate) }
            catch (error) { throw error }
          })
        ),
        {
          pending: 'Préparation de votre téléchargement',
          error: { render({ data }) { return `Erreur : ${((data as HTTPResponse)?.error) || JSON.stringify(data)}` } },
          success: 'Document(s) téléchargé(s) avec succès'
        });
    } catch (error) {
      console.error(error);
    }

    setIsDownloading(false);
  };



  /**
   * Get a React Component matching a given MySpaceRenderableComponent key
   * @author Mickaël "Naxe67" APPEL
   * @since 2024-04-15
   * @param {MySpaceRenderableComponent} index 
   * @returns {React.ReactNode}
  */
  const getComponentFromIndex = (index: MySpaceRenderableComponent) => {
    switch (index) {

      // Document Deposit
      case MySpaceRenderableComponent.DocumentDeposit:
        return <DocumentDeposit key={`documentdeposit-${dossier.DOS_REF}-${dossiers.length}`} dossiers={dossiers} dossier={dossier}></DocumentDeposit>;

      case MySpaceRenderableComponent.SentDocuments:
        return (<SentDocuments dossier={{} as Dossier}></SentDocuments>);

      case MySpaceRenderableComponent.BCTutorials:
        return (<BCTutorials />);

      // New documents / Home
      case MySpaceRenderableComponent.Home:
      default:
        /* New published documents table */
        return (<div className='m-4 my-2 lg:my-4'>
          {/* Title */}
          <div className="font-bold my-2 grow">
            <span className='text-md lg:text-lg'><FontAwesomeIcon className={`w-6 h-6 lg:w-8 lg:h-8`} icon={faBell} style={{ color: cabinet.color + 'CF' }} /> Nouveaux documents</span>
            <hr className={`mt-1 border-1 opacity-100 rounded-lg w-[30%]`} style={{ borderColor: cabinet.color }} />
          </div>

          {/* New documents Table */}
          <BCTable key={'documents-' + tableRows.length} customKey='documents' emptyMsg={'Aucun nouveau document n\'a été publié'} paginationMsg={'Document [0] à [1] sur [2]'} sortSettings={{ colName: 'Date', order: SortOrder.desc }}
            columns={[
              { 'name': 'id', show: false }, // ID
              { 'name': 'Date', show: true, className: 'text-left w-[100px]', formatBeforeRender: (row: TableRow, colKey: string) => dateToDDMMYYYY(row[colKey]), sort: SortType.Date, usedForResearch: true },
              { 'name': 'DOS_REF', show: false },
              { 'name': 'Dossier', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true },
              { 'name': 'Type', show: true, className: 'text-left w-[80px]', formatBeforeRender: (row: TableRow, colKey: string) => getTypeImg(row[colKey], true), sort: SortType.Alphanumeric },
              { 'name': 'Nom', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true }
            ]} data={tableRows} selection={true}
            buttons={[{ name: 'Télécharger', icon: faDownload, action: (selectedRows: TableRow[]) => { DownloadSelectedDocs(selectedRows); } }]}
            /*onRowClick={(row: TableRow) => navigateToDocument(row)}*/ />


          {/* Upcoming actions Title */}
          { cabinet.permissions?.isPlanningEnabled ? <div>
            <div className="font-bold mb-2 mt-8 grow">
              <div className='text-md lg:text-lg text-black'><FontAwesomeIcon className={`w-6 h-6 lg:w-8 lg:h-8`} icon={faBell} style={{ color: cabinet.color + 'CF' }} /> Evènements à venir</div>
              <hr className={`mt-1 border-1 opacity-100 rounded-lg w-[30%]`} style={{ borderColor: cabinet.color }} />
            </div>

            {/* Upcoming actions Table */}
            <BCTable id="actions-table" key={'actions-' + actionsTableRows.length} customKey='actions' emptyMsg={'Aucun évènement à venir'} paginationMsg={'Evénement [0] à [1] sur [2]'} sortSettings={{ colName: 'Date', order: SortOrder.asc }}
              columns={[
                { 'name': 'id', show: false }, // ID
                { 'name': 'Date', show: true, className: 'text-left w-[150px]', formatBeforeRender: (row: TableRow, colKey: string) => dateToDDMMYYYY(row[colKey], true), sort: SortType.Date, usedForResearch: true },
                { 'name': 'DOS_REF', show: false },
                { 'name': 'Dossier', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true },
                { 'name': 'Lieu', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true },
                { 'name': 'Descriptif', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true },
                { 'name': 'Intervenant', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true },
                { 'name': 'Commentaire', show: true, className: 'text-left', sort: SortType.Alphanumeric, usedForResearch: true },
              ]} data={actionsTableRows} selection={false}
              buttons={[{ name: 'Imprimer', icon: faPrint, action: async (selectedRows: TableRow[]) => { printElement(document.getElementById('actions-table'), { pageTitle: 'Evènements à venir', logoBase64: cabinet.logo, orientation: 'landscape' }); }, withoutSelection: true }]}
              /*onRowClick={(row: TableRow) => navigateToDocument(row)}*/ />
          </div> : <></>}
        </div>);
    }
  }

  // Handle conditional components rendering (folder-relative component or generic one)
  //console.log(state?.renderedComponent);
  const [componentRenderedIndex, setComponentRenderedIndex] = useState<MySpaceRenderableComponent>(state?.renderedComponent || MySpaceRenderableComponent.Home);


  // JSX Template
  return (
    <div className='flex flex-col flex-1 h-full'>

      {/* Header contaning logo + folderinput + profile */}
      <Header showFolderInput={true} dossier={{}} onLogoClick={setComponentRenderedIndex} />


      {/* Rest of the page */}
      <div className='flex flex-row flex-grow w-[inherit]'>

        { /* SideBar */}
        <SideBar dossier={{}} changeRenderedComponent={setComponentRenderedIndex} expanded={sidebarExpanded} setExpanded={setSidebarExpanded} />

        {/* Actual content without sidebar or header */}
        <div className='w-full flex-grow shadow overflow-auto'>

          {/* Display potential errors */}
          {state?.error && <div className='text-2xl text-center m-auto'>{state?.error}</div>}

          {/* Content + Banner */}
          <div className='flex flex-col h-full max-h-[90vh]'>

            {/* Content */}
            {/* If no custom component has been provided it means that we need to display new published documents */}
            <div className='flex-grow overflow-auto'>
              {/* Component to render */}
              {getComponentFromIndex(componentRenderedIndex)}
            </div>

            {/* Banner */}
            <div className="flex flex-row max-h-[20vh] mt-4 grow items-center justify-center w-full">
              <a href={`${cabinet?.banner?.url}`} target='__blank' className={`w-full h-full flex ${!(cabinet?.banner?.url) || cabinet?.banner?.url === '' ? 'hidden' : ''}`}>
                <img
                  src={cabinet?.banner?.image}
                  alt="Banner"
                  className={`w-auto self-end object-contain h-[inherit] max-h-[20vh] object-contain object-bottom mx-auto ${!(cabinet?.banner?.image) || cabinet?.banner?.image === '' ? 'hidden' : ''}`}
                />
              </a>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};
